import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Table, Badge, Button, Spinner, Form, Offcanvas } from "react-bootstrap";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  FaUniversity, 
  FaAward, 
  FaInfoCircle, 
  FaChevronRight, 
  FaSearch, 
  FaGraduationCap, 
  FaMicroscope, 
  FaChalkboardTeacher, 
  FaBookOpen,
  FaFilter 
} from "react-icons/fa";

import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";

const ProgramsPage = () => {
  const [undergraduate, setUndergraduate] = useState([]);
  const [graduate, setGraduate] = useState([]);
  const [postgraduate, setPostgraduate] = useState([]);
  const [other, setOther] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("Undergraduate");
  const [showDrawer, setShowDrawer] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setIsSyncing(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fetch/programsData`);
      const data = response.data[0];
      setUndergraduate(data.undergraduate || []);
      setGraduate(data.graduate || []);
      setPostgraduate(data.postgraduate || []);
      setOther(data.other || null);
    } catch (err) {
      console.error("Failed to sync academic programs.");
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(() => fetchData(true), 60000); // Sync every minute
    return () => clearInterval(intervalId);
  }, [fetchData]);

  // Define Categories for Sidebar
  const categories = useMemo(() => [
    { id: "Undergraduate", label: "Undergraduate", icon: <FaGraduationCap />, count: undergraduate.length },
    { id: "Graduate", label: "Graduate Programs", icon: <FaBookOpen />, count: graduate.length },
    { id: "Postgraduate", label: "Postgraduate / PhD", icon: <FaMicroscope />, count: postgraduate.length },
    { id: "Special", label: "Special Programs", icon: <FaChalkboardTeacher />, count: 3 },
  ], [undergraduate.length, graduate.length, postgraduate.length]);

  // Logic to get data for the active view
  const currentList = useMemo(() => {
    let list = [];
    if (activeCategory === "Undergraduate") list = undergraduate;
    else if (activeCategory === "Graduate") list = graduate;
    else if (activeCategory === "Postgraduate") list = postgraduate;
    
    if (searchTerm) {
      return list.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        p.department.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return list;
  }, [activeCategory, undergraduate, graduate, postgraduate, searchTerm]);

  const handleSelect = (id) => {
    setActiveCategory(id);
    setSearchTerm("");
    setShowDrawer(false);
  };

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2">CATALOGING PROGRAMS</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Replicated Sub-Nav Styling for Perfect Synchronization */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <h1 className="h4 fw-900 text-navy m-0">Course <span className="text-primary">explorer</span></h1>
              <small className="text-muted d-flex align-items-center gap-2">
                 <span className="sync-dot-mini"></span> {isSyncing ? "Syncing catalogs..." : "Official Listing 2024-25"}
              </small>
            </Col>
            <Col md={7}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <div className="search-pill-container flex-grow-1">
                    <FaSearch className="search-icon" />
                    <Form.Control 
                        type="text" 
                        placeholder="Search by program or department..." 
                        className="border-0 shadow-none bg-transparent"
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {/* Mobile Filter Toggle */}
                 <Button 
                    variant="primary" 
                    className="rounded-pill px-4 d-lg-none d-flex align-items-center gap-2 nav-hint-pulse shadow-sm h-100" 
                    onClick={() => setShowDrawer(true)}
                 >
                    <FaFilter /> <span className="fw-bold small">Level</span>
                 </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Desktop Sidebar */}
            <Col lg={4} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">ACADEMIC LEVELS</div>
                  <div className="program-list custom-scrollbar">
                    {categories.map((cat) => (
                      <button 
                        key={cat.id} 
                        className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeCategory === cat.id ? 'is-active' : ''}`}
                        onClick={() => handleSelect(cat.id)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className={`icon-circle-mini ${activeCategory === cat.id ? 'active' : ''}`}>
                            {cat.icon}
                          </div>
                          <div className="text-start">
                            <div className="name fw-bold mb-0">{cat.label}</div>
                            <div className="stats-tag">{cat.count} Programs</div>
                          </div>
                        </div>
                        <FaChevronRight className={`chevron-mini ${activeCategory === cat.id ? 'visible' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Institutional Links Refined */}
                <div className="sidebar-card mt-4 border-0 shadow-sm p-4 bg-white">
                    <h6 className="fw-800 text-navy mb-3 small letter-spacing-1">UNIVERSITY LINKS</h6>
                    <div className="d-grid gap-2">
                        <Button variant="light-soft" href="https://www.scimagoir.com/institution.php?idp=11615" target="_blank" className="text-start d-flex align-items-center text-navy fw-700 py-3 rounded-4 border">
                            <FaAward className="me-3 text-primary fs-5" /> Global Ranking
                        </Button>
                        <Button variant="light-soft" href="https://www.iba-suk.edu.pk/international-accreditation" target="_blank" className="text-start d-flex align-items-center text-navy fw-700 py-3 rounded-4 border">
                            <FaUniversity className="me-3 text-primary fs-5" /> Accreditations
                        </Button>
                    </div>
                </div>
              </div>
            </Col>

            {/* Main Content Catalog */}
            <Col lg={8}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                  className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm"
                >
                  <div className="section-intro mb-5">
                    <div className="category-tag">Academic Catalog</div>
                    <h2 className="portal-heading text-navy display-5 mb-3">{activeCategory} Programs</h2>
                    <div className="ivy-divider mb-4" />
                    <p className="editorial-text-body text-muted">Explore our diverse range of {activeCategory.toLowerCase()} programs designed to produce global leaders and innovators.</p>
                  </div>

                  {activeCategory === "Special" ? (
                    <Row className="g-4">
                      {['summer', 'crash', 'foundation'].map((key) => (
                        <Col xs={12} key={key}>
                          <div className="phase-block bg-white p-4 rounded-4 shadow-sm border-top border-primary border-4 hover-translate-y transition-all">
                             <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="h5 fw-800 text-primary text-uppercase m-0">{key} Program</h4>
                                <Badge bg="soft-primary" className="text-primary px-3 rounded-pill fw-bold">Active</Badge>
                             </div>
                            <p className="editorial-text-body text-muted small mb-4">{other?.[key]?.description || "Short-term academic tracks designed for rapid skill acquisition and foundational strength."}</p>
                            <Button as={Link} to={other?.[key]?.link || "#"} variant="outline-primary" className="rounded-pill px-4 fw-bold">
                                Program Guidelines <FaChevronRight className="ms-2 small" />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="bg-white rounded-4 overflow-hidden">
                      <Table responsive hover className="mb-0 premium-fee-table">
                        <thead>
                          <tr>
                            <th className="ps-4">PROGRAM NAME</th>
                            <th>DEPARTMENT</th>
                            <th className="text-center">DURATION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentList.length > 0 ? (
                            currentList.map((program) => (
                              <tr key={program.id}>
                                <td className="ps-4 py-3">
                                  <Link 
                                    to={`/program-details/${program.id}`} 
                                    className="text-decoration-none text-primary fw-bold hover-underline d-block mb-1"
                                  >
                                    {program.name}
                                  </Link>
                                  {program.major && <div className="text-muted small italic">{program.major}</div>}
                                </td>
                                <td className="py-3 text-muted fw-500">{program.department}</td>
                                <td className="py-3 text-center">
                                  <span className="date-display">{program.length}</span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center py-5 border-dashed rounded-4">
                                <FaInfoCircle className="mb-3 opacity-20" size={50} />
                                <h5 className="text-muted fw-bold">No Records Found</h5>
                                <p className="small text-muted mb-0">Try adjusting your search or filters.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MOBILE CATEGORY DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-75 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Academic Level</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 bg-light-soft">
          <div className="program-list-mobile">
            {categories.map((cat) => (
                <button 
                key={cat.id} 
                className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeCategory === cat.id ? 'is-active' : ''}`}
                onClick={() => handleSelect(cat.id)}
                >
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle-mini ${activeCategory === cat.id ? 'active' : ''}`}>
                        {cat.icon}
                    </div>
                    <div className="text-start">
                        <div className="name fw-bold mb-0">{cat.label}</div>
                        <div className="stats-tag">{cat.count} Programs Available</div>
                    </div>
                </div>
                {activeCategory === cat.id && <FaChevronRight className="chevron-mini visible" />}
                </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .text-navy { color: #002b5a; }
        
        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        .editorial-text-body { font-size: 1.1rem; line-height: 1.6; color: #475569; }

        .sync-dot-mini {
          width: 8px; height: 8px; background: #10b981;
          border-radius: 50%; display: inline-block;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .search-pill-container {
          background: #f1f4f9; border-radius: 50px; padding: 10px 25px;
          display: flex; align-items: center; transition: 0.3s;
        }
        .search-pill-container:focus-within { background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.05); border: 1px solid #0055a4; }
        .search-pill-container input { border: none; background: transparent; outline: none; width: 100%; margin-left: 12px; font-weight: 500; color: #002b5a; }
        .search-icon { color: #94a3b8; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
          cursor: pointer;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }
        .icon-circle-mini.active { background: #002b5a; color: #fff; border-color: #002b5a; }

        .stats-tag { font-size: 0.72rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }
        .prog-item.is-active .chevron-mini.visible { color: #ffc300; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }
        
        .premium-fee-table thead th { border-top: none; color: #64748b; font-weight: 800; background: #fdfdfd; padding: 15px; border-bottom: 2px solid #f1f5f9; font-size: 0.75rem; letter-spacing: 1px; }
        .premium-fee-table tbody td { vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
        
        .date-display {
          background: #f0f4f8; color: #0055a4; padding: 6px 16px;
          border-radius: 50px; font-weight: 800; font-size: 0.8rem;
        }

        .hover-underline:hover { text-decoration: underline !important; }
        .hover-translate-y:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.08) !important; }
        .transition-all { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .italic { font-style: italic; }
        .fw-500 { font-weight: 500; }
        .border-dashed { border: 2px dashed #e2e8f0 !important; }

        .premium-loader { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .section-standard-spacing { padding-bottom: 30px; }
          .portal-heading { font-size: 1.6rem; word-break: break-word; letter-spacing: -1px; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .details-card { padding: 1rem !important; }
          .editorial-text-body { font-size: 0.95rem; }
          
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 18px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 85, 164, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          border: 2px solid #fff !important;
        }
      `}</style>
    </div>
  );
};

export default ProgramsPage;
